/** Generic list helpers used by the dashboard (pure, unit-testable). */

export type MoveDirection = 'up' | 'down';

/**
 * Moves the item at `index` one position up/down and returns a new array.
 * Mutates nothing; out-of-range moves return the original order.
 */
export function moveItem<T>(items: T[], index: number, direction: MoveDirection): T[] {
  const target = direction === 'up' ? index - 1 : index + 1;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) {
    return [...items];
  }
  const next = [...items];
  const current = next[index] as T;
  next[index] = next[target] as T;
  next[target] = current;
  return next;
}

/** Re-sequences a reordered list into `{ id, sort_order }` updates. */
export function resequence<T extends { id: string }>(items: T[]): Array<{ id: string; sort_order: number }> {
  return items.map((item, index) => ({ id: item.id, sort_order: index }));
}

export interface OrderFilter {
  status?: string;
  payment?: string;
  query?: string;
}

export interface FilterableOrder {
  status: string;
  payment_method: string;
  payment_status: string;
  order_number: number;
  customer_name: string;
}

export function filterOrders<T extends FilterableOrder>(orders: T[], filter: OrderFilter): T[] {
  const query = filter.query?.trim().toLowerCase() ?? '';
  return orders.filter((order) => {
    if (filter.status && filter.status !== 'all' && order.status !== filter.status) return false;
    if (filter.payment && filter.payment !== 'all' && order.payment_method !== filter.payment) {
      return false;
    }
    if (query.length > 0) {
      const haystack = `${order.order_number} ${order.customer_name}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });
}

/** Groups items by a `YYYY-MM-DD` key derived from `dateKey`, newest-first keys. */
export function groupByDay<T>(items: T[], dateKey: (item: T) => string): Array<[string, T[]]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = dateKey(item).slice(0, 10);
    const bucket = map.get(key);
    if (bucket) bucket.push(item);
    else map.set(key, [item]);
  }
  return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}
